'use client';

import React from 'react';
import { useProductStock } from '@/hooks/useProducts';

const OUT_OF_STOCK_LABEL: Record<string, string> = {
  es: 'Sin stock',
  en: 'Out of stock',
};

function getOutOfStockLabel(lang: string): string {
  return OUT_OF_STOCK_LABEL[lang] ?? OUT_OF_STOCK_LABEL.es;
}

export interface StockBadgeProps {
  productId: number;
  lang?: string;
  className?: string;
  variant?: 'ribbon' | 'compact' | 'compact-vertical';
}

const StockBadge: React.FC<StockBadgeProps> = ({
  productId,
  lang = 'es',
  className = '',
  variant = 'ribbon',
}) => {
  const { data: totalStock, isSuccess } = useProductStock(productId, lang);

  if (!isSuccess || totalStock == null || totalStock > 0) {
    return null;
  }

  const label = getOutOfStockLabel(lang);

  if (variant === 'compact') {
    return (
      <div data-component-id="src/components/common/StockBadge.tsx:38:6" data-component-path="src/components/common/StockBadge.tsx" data-component-line="38" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`absolute top-0 left-0 pointer-events-none z-10 ${className}`}
        role="status"
        aria-label={label}
      >
        <div data-component-id="src/components/common/StockBadge.tsx:43:8" data-component-path="src/components/common/StockBadge.tsx" data-component-line="43" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-sm%20bg-%5B%239F2B00%5D%20px-1.5%20py-0.5%20text-%5B8px%5D%20leading-none%20font-tt-norms-bold%20tracking-%5B0.03em%5D%20text-white%20uppercase%20shadow-sm%22%7D" className="rounded-sm bg-[#9F2B00] px-1.5 py-0.5 text-[8px] leading-none font-tt-norms-bold tracking-[0.03em] text-white uppercase shadow-sm">
          {label}
        </div>
      </div>
    );
  }

  if (variant === 'compact-vertical') {
    const words = label.split(' ').filter(Boolean);

    return (
      <div data-component-id="src/components/common/StockBadge.tsx:54:6" data-component-path="src/components/common/StockBadge.tsx" data-component-line="54" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`absolute top-0 left-0 pointer-events-none z-10 ${className}`}
        role="status"
        aria-label={label}
      >
        <div data-component-id="src/components/common/StockBadge.tsx:59:8" data-component-path="src/components/common/StockBadge.tsx" data-component-line="59" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20w-3.5%20rounded-r-sm%20bg-%5B%239F2B00%5D%20text-white%20text-%5B6px%5D%20leading-%5B6px%5D%20font-tt-norms-bold%20uppercase%20shadow-sm%20flex%20items-center%20justify-center%20py-0.5%22%7D" className="h-12 w-3.5 rounded-r-sm bg-[#9F2B00] text-white text-[6px] leading-[6px] font-tt-norms-bold uppercase shadow-sm flex items-center justify-center py-0.5">
          <span data-component-id="src/components/common/StockBadge.tsx:60:10" data-component-path="src/components/common/StockBadge.tsx" data-component-line="60" data-component-file="StockBadge.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20gap-%5B2px%5D%22%7D" className="flex flex-col items-center gap-[2px]">
            {words.map((word, wordIndex) => (
              <span data-component-id="src/components/common/StockBadge.tsx:62:14" data-component-path="src/components/common/StockBadge.tsx" data-component-line="62" data-component-file="StockBadge.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20flex-col%20items-center%22%7D" key={`${word}-${wordIndex}`} className="flex flex-col items-center">
                {word.split('').map((char, charIndex) => (
                  <span data-component-id="src/components/common/StockBadge.tsx:64:18" data-component-path="src/components/common/StockBadge.tsx" data-component-line="64" data-component-file="StockBadge.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" key={`${char}-${charIndex}`}>{char}</span>
                ))}
              </span>
            ))}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/components/common/StockBadge.tsx:75:4" data-component-path="src/components/common/StockBadge.tsx" data-component-line="75" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`absolute top-0 left-0 w-32 h-32 overflow-hidden pointer-events-none z-10 ${className}`}
      role="status"
      aria-label={label}
    >
      <div data-component-id="src/components/common/StockBadge.tsx:80:6" data-component-path="src/components/common/StockBadge.tsx" data-component-line="80" data-component-file="StockBadge.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-%5B18px%5D%20left-%5B-32px%5D%20w-%5B140px%5D%20-rotate-45%20bg-%5B%239F2B00%5D%20text-white%20text-%5B10px%5D%20leading-none%20font-tt-norms-bold%20tracking-%5B0.03em%5D%20py-1.5%20text-center%20uppercase%20shadow-sm%22%7D" className="absolute top-[18px] left-[-32px] w-[140px] -rotate-45 bg-[#9F2B00] text-white text-[10px] leading-none font-tt-norms-bold tracking-[0.03em] py-1.5 text-center uppercase shadow-sm">
        {label}
      </div>
    </div>
  );
};

export default StockBadge;